import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import './DiversitechBanner.scss';
import DotSeparator from "./DotSeparator";

const DiversitechBanner: React.FC = () => {
  const [repetitions, setRepetitions] = useState(3);
  const [xFinal, setXFinal] = useState('-50%');
  const textRef = useRef<HTMLSpanElement>(null);

  const bannerTextParts = [
    "Join us March 19-21 in Philadelphia for ",
    <a
      href="https://diversitech.tribaja.co/"
      target="_blank"
      rel="noopener noreferrer"
      className="banner-link"
      key="diversitech-link"
    >
      Diversitech
    </a>,
    "! Inclusion in Tech starts HERE!"
  ];

  useEffect(() => {
    // Calculates how many repetitions of the banner text are needed for seamless scrolling
    const calculateRepetitions = () => {
      if (!textRef.current) return;
      // Create a temporary element to measure the width of the banner text
      const tempSpan = document.createElement('span');
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.fontSize = '16px';
      tempSpan.style.fontWeight = '600';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.textContent = bannerTextParts.map(part => typeof part === 'string' ? part : part.props.children).join('');
      document.body.appendChild(tempSpan);
      const textWidth = tempSpan.getBoundingClientRect().width;
      document.body.removeChild(tempSpan);
      const screenWidth = window.innerWidth;
      // Calculate how many repetitions are needed to fill 3x the screen width for smooth infinite scroll
      const neededReps = Math.ceil((screenWidth * 3) / textWidth);
      setRepetitions(Math.max(neededReps, 4)); // Minimum 4 repetitions
    };

    // Returns the correct xFinal value based on the current screen width
    const getXFinal = () => {
      return window.innerWidth < 480 ? '-100%' : '-50%';
    };

    calculateRepetitions();
    setXFinal(getXFinal());

    // Updates xFinal value on window resize
    const handleResize = () => {
      setXFinal(getXFinal());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [bannerTextParts]);
  
  const duration = 15;

  return (
    <motion.div
      className="diversitech-banner"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="diversitech-banner__ticker">
        <motion.div
          className="diversitech-banner__content"
          animate={{ x: ['0%', xFinal] }}
          transition={{
            duration,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          <span className="banner-text" ref={textRef}>
            {Array.from({ length: repetitions }).map((_, i) => (
              <React.Fragment key={i}>
                {bannerTextParts}
                <DotSeparator size={10} />
              </React.Fragment>
            ))}
          </span>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default DiversitechBanner;